const modalFocusMixin = {
  data: () => ({
    pseudoElId: null,
    returnFocusToAfterClose: null,
    isKeyboardNavigationActive: false
  }),

  computed: {
    pseudoFocusElementId () {
      return `pseudo-focus-element-${this.pseudoElId}`
    },

    modalTitleId () {
      if (!this.modalId) return undefined
      return `${this.modalId}___BV_modal_title_`
    }
  },

  methods: {
    onModalDisplayed (elementRef) {
      // Bootstrap detects which element was used to open the modal ($refs?.smodal?.$_returnFocus). In that case,
      // it will hold a element reference.
      // If returnFocus prop was used, we need to select the element first.
      this.returnFocusToAfterClose = this.selectReturnFocusElement(elementRef)

      // add pseudo focus element for iOS
      if (!this.isIOS) return
      this.addPseudoFocusElement()
    },

    async onModalHidden (preventFocusReturnOnModalClose = false) {
      !preventFocusReturnOnModalClose && await this.returnFocusTo(this.returnFocusToAfterClose)
      this.returnFocusToAfterClose = null

      // clean-up: remove pseudo focus element if it was created before
      this.removePseudoFocusElement()
    },

    async setInitialFocusElement (isConfirmModal = false) {
      // No focus when running e2e tests, as it will confuse cypress selectors
      if (window.Cypress) return

      // Screenreaders: Set focus to the title
      if (this.isScreenReaderActive) return this.setFirstFocusToTitle(isConfirmModal)

      // No focus handling for mouse-users
      if (!this.isKeyboardNavigationActive) return

      // Focus for keyboard navigation: First interactive element
      await this.setFirstFocusOnFirstInteractiveElement(isConfirmModal)
    },

    async setFirstFocusToTitle (isConfirmModal = false) {
      const selector = isConfirmModal ? '.modal-title' : `[id="${this.modalTitleId}"]`
      const modalTitle = this.$refs?.smodal?.$refs?.modal?.querySelector(selector)
      await this.setFocusToElement(modalTitle, true)
    },

    async setFirstFocusOnFirstInteractiveElement (isConfirmModal = false) {
      const interactiveElements = 'a, button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      // For confirmation modals, we select the first interactive element of the modal-footer (negative action)
      const targetContainer = isConfirmModal ? this.$refs?.smodal?.$refs?.footer : this.$refs?.smodal?.$refs?.modal
      const firstInteractiveElement = targetContainer?.querySelector(interactiveElements)
      await this.setFocusToElement(firstInteractiveElement)
    },

    async setFocusToElement (targetElement, trapFocusInPseudo = false) {
      if (!targetElement) return

      // iOS (and sometimes android as well) is a little special. Setting the focus by just calling el.focus() does work
      // visually (we can see the focus frame) but it messes up the tab order. To overcome this, we dynamically
      // add/remove a pseudo focus element to the DOM and focus it first.
      if ((this.isIOS || trapFocusInPseudo) && this.isScreenReaderActive) {
        // Set and trap the focus to the pseudo element for a duration of 250ms (seems to be the sweet spot for iOS)
        await this.trapFocusInPseudoElementForDuration(this.isIOS ? 250 : 150)
      }

      // finally set focus to the actual element we want to have focussed
      if (!this.isIOS) {
        await this.$nextTick()
        return targetElement.focus()
      }
      setTimeout(() => {
        targetElement.focus()
      }, 200)
    },

    /**
     * Returns focus to the element that triggered the modal.
     * NOTE: This logic was copied from Bootstrap's modal.js and adapted to work with iOS. On iOS, the focus-return
     * breaks the tab order. To overcome this, we add a pseudo focus element to the DOM and focus it first.
     * @returns {Promise<void>}
     */
    async returnFocusTo (returnFocusToAfterCloseElement) {
      if (!returnFocusToAfterCloseElement) return
      // Possibly could be a component reference
      return await this.setFocusToElement(returnFocusToAfterCloseElement.$el || returnFocusToAfterCloseElement, true)
    },

    trapFocusInPseudoElementForDuration (duration) {
      return new Promise((resolve) => {
        const startTime = Date.now()

        const loop = () => {
          // Check if the elapsed time is less than the duration
          if (Date.now() - startTime < duration) {
            // focus the pseudo element
            const pseudoEl = document.getElementById(this.pseudoFocusElementId)
            pseudoEl?.focus()

            // Use setTimeout to continue the loop
            setTimeout(loop, 0)
          } else {
            return resolve()
          }
        }

        // Start the loop
        loop()
      })
    },

    selectReturnFocusElement (elementRef) {
      return typeof elementRef === 'string' ? document.querySelector(elementRef) : elementRef
    },

    addPseudoFocusElement () {
      // it seems like input elements are working better than buttons for focus pseudo elements
      // inputmode none to prevent keyboard from showing
      // position fixed to prevent scrolling
      // opacity 1 to make sure mobile safari can focus it
      // font-size 16px to disable iOS zooming
      this.$refs?.smodalWrapper?.insertAdjacentHTML(
        'beforeend',
        `<input id="${this.pseudoFocusElementId}" inputmode="none" style="position:fixed; opacity:1; height:0; width:0; top:0; left:0; font-size: 16px;" />`
      )
    },

    removePseudoFocusElement () {
      const el = document.getElementById(this.pseudoFocusElementId)
      if (!el) return
      el.remove()
    }
  },

  created () {
    this.pseudoElId = this.$uuid()
  },

  beforeMount () {
    this.isKeyboardNavigationActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isKeyboardNavigationActive
  },

  watch: {
    '$store.state.isKeyboardNavigationActive' (newVal) {
      this.isKeyboardNavigationActive = newVal
    }
  }
}

export default modalFocusMixin
